import { createAsyncThunk } from '@reduxjs/toolkit';
import { ActiveStore, ActiveDiningTable, CheckoutRequestState } from './activeStoreSlice';
import { createApiRequest } from '../../utils/apiUtils';
import store from '../Store';

const getToken = () => localStorage.getItem('accessToken');

export const fetchActiveStore = createAsyncThunk<ActiveStore | null, string>(
  'activeStore/fetchActiveStore',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await createApiRequest<ActiveStore>(`/stores/${storeId}/active`, {
        method: 'GET',
        requiresAuth: true,
      });
      
      // For fetchActiveStore, null is a valid response when store is closed
      if (!response && response !== null) {
        return rejectWithValue('No data received from server');
      }
      
      return response;
    } catch (error) {
      if ((error as Error).message === 'HTTP error! status: 404') {
        return null; // 店舗が閉店中の場合はnullを返す
      }
      return rejectWithValue((error as Error).message);
    }
  }
);

export const openStore = createAsyncThunk<void, string>(
  'activeStore/openStore',
  async (storeId, { dispatch, rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/active/store/open`, {
        method: 'POST',
        requiresAuth: true,
      });
      dispatch(fetchActiveStore(storeId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const closeStore = createAsyncThunk<void, string>(
  'activeStore/closeStore',
  async (storeId, { dispatch, rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/active/store/close`, {
        method: 'PUT',
        requiresAuth: true,
      });
      dispatch(fetchActiveStore(storeId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const startDiningTable = createAsyncThunk<
  ActiveDiningTable,
  {
    storeId: string;
    activeStoreId: string;
    diningTableId: string;
    numberOfVisitors: number;
  }
>(
  'activeStore/startDiningTable',
  async ({ storeId, activeStoreId, diningTableId, numberOfVisitors }, { rejectWithValue }) => {
    try {
      const response = await createApiRequest<ActiveDiningTable>(`/stores/${storeId}/active/${activeStoreId}/dining-table/start`, {
        method: 'POST',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ diningTableId, numberOfVisitors }),
      });
      
      if (!response) {
        return rejectWithValue('No data received from server');
      }
      
      return response;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const endDiningTable = createAsyncThunk<void, { storeId: string, diningTableId: string }>(
  'activeStore/endDiningTable',
  async ({ storeId, diningTableId }, { dispatch, rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/active/dining-table/end`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ diningTableId }),
      });
      dispatch(fetchActiveStore(storeId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const closeDiningTable = createAsyncThunk<void, { storeId: string, activeStoreId: string, activeDiningTableId: string }>(
  'activeStore/closeDiningTable',
  async ({ storeId, activeStoreId, activeDiningTableId }, { dispatch, rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/active/${activeStoreId}/dining-table/end`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activeDiningTableId }),
      });
      dispatch(fetchActiveStore(storeId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const moveDiningTable = createAsyncThunk<
  void,
  {
    storeId: string;
    activeStoreId: string;
    activeDiningTableId: string;
    toDiningTableId: string;
  }
>(
  'activeStore/moveDiningTable',
  async ({ storeId, activeStoreId, activeDiningTableId, toDiningTableId }, { dispatch, rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/active/${activeStoreId}/dining-table/move`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activeDiningTableId, toDiningTableId }),
        // 成功後のリトライは「移動元と移動先が同じ」の400になり紛らわしいためリトライしない
        retryOnError: false,
      });
      dispatch(fetchActiveStore(storeId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchBill = createAsyncThunk<any, { activeStoreId: string, activeDiningTableId: string }>(
  'activeStore/fetchBill',
  async ({ activeStoreId, activeDiningTableId }, { rejectWithValue }) => {
    try {
      return await createApiRequest(`/active-stores/${activeStoreId}/orders/dining-tables/${activeDiningTableId}/bill`, {
        method: 'GET',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const checkoutBillOrder = createAsyncThunk<void, { storeId: string, activeStoreId: string, activeDiningTableId: string }>(
  'activeStore/checkoutBillOrder',
  async ({ storeId, activeStoreId, activeDiningTableId }, { dispatch, rejectWithValue }) => {
    try {
      await createApiRequest(`/active-stores/${activeStoreId}/orders/dining-tables/${activeDiningTableId}/checkout`, {
        method: 'PUT',
        requiresAuth: true,
        retryOnError: false,  // チェックアウトはリトライしない
      });
      dispatch(fetchActiveStore(storeId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);
// 会計リクエストの受領（REQUESTED → ACKNOWLEDGED）。ADR 0068。
// 一覧の再取得は呼び出し側で fetchActiveStore を dispatch する（受領後の表示更新と SSE 同期を同じ経路に揃えるため）。
export const acknowledgeCheckoutRequest = createAsyncThunk<
  CheckoutRequestState,
  { activeStoreId: string; activeDiningTableId: string }
>(
  'activeStore/acknowledgeCheckoutRequest',
  async ({ activeStoreId, activeDiningTableId }, { rejectWithValue }) => {
    try {
      const response = await createApiRequest<CheckoutRequestState>(
        `/active-stores/${activeStoreId}/orders/dining-tables/${activeDiningTableId}/checkout-request/acknowledge`,
        {
          method: 'PUT',
          requiresAuth: true,
        }
      );
      if (!response) {
        return rejectWithValue('No data received from server');
      }
      return response;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// 会計リクエストの取消（REQUESTED / ACKNOWLEDGED → NONE）。顧客の誤操作・店員の誤受領の取り消し用。ADR 0068。
export const cancelCheckoutRequest = createAsyncThunk<
  CheckoutRequestState,
  { activeStoreId: string; activeDiningTableId: string }
>(
  'activeStore/cancelCheckoutRequest',
  async ({ activeStoreId, activeDiningTableId }, { rejectWithValue }) => {
    try {
      const response = await createApiRequest<CheckoutRequestState>(
        `/active-stores/${activeStoreId}/orders/dining-tables/${activeDiningTableId}/checkout-request/cancel`,
        {
          method: 'PUT',
          requiresAuth: true,
        }
      );
      if (!response) {
        return rejectWithValue('No data received from server');
      }
      return response;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);
